import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import type { VideoBoxReadings } from '@/components/molecules/VideoBox';
import type { LocationStream } from '@/domain/videoStream';

/** A stream's state, from the location stream list or the bioscope/livestream topic. */
export interface MuxData {
    streamId?: string;
    status?: string;
    timestamp?: string | number;
    userId: number | null;
    playbackId: string | null;
}

interface DailyQuota {
    bioscopeVersion?: string;
    quotaAvailable?: boolean;
}

/** A pen's row of the location stream list, with what the video page knows about it. */
export interface LocationVideoCage extends LocationStream {
    muxData?: MuxData;
    online: boolean | undefined;
    isButtonDisabled: boolean;
    isLoading: boolean;
    icon: 'Loading' | 'Streaming' | 'Error' | 'Offline' | 'Online';
    isButtonSelected: boolean;
    isOutOfQuota?: boolean;
    dailyQuotaAvailable?: DailyQuota;
    isInvalidBioscope?: boolean;
    bioscopeData?: VideoBoxReadings;
}

export type AllButtonState = {
    isSelected: boolean;
    isDisabled: boolean;
};

/** A stream request the page sends for one bioscope (start, stop, disable). */
type StreamRequest = (request: { bioscopeId: number }) => unknown;

export interface LocationVideoState {
    activeCages: LocationVideoCage[];
    locationName: string;
    onlyViewMode: boolean;
    allButtonState: AllButtonState;

    initializeCages: (
        activeCages: LocationStream[],
        startStream: StreamRequest,
        stopStream: StreamRequest,
        disableStream: StreamRequest
    ) => void;
    updateBioscopeOnlineStatus: (bioscopeId: number, status: unknown) => void;
    buttonActionHandler: (bioscopeId: number | null, value?: boolean) => void;
    startStream: StreamRequest;
    stopStream: StreamRequest;
    disableStream: StreamRequest;
    updateMuxData: (bioscopeId: number | null, muxData: MuxData) => void;
    updateBioscopeData: (bioscopeId: number | null, data: VideoBoxReadings) => void;
    updateAllButtonState: () => void;
    setOnlyViewMode: (onlyViewMode: boolean) => void;
    setIsButtonDisabled: (bioscopeId: number, isButtonDisabled: boolean) => void;
}

export const useLocationVideoStore = create<LocationVideoState>()(
    immer((set) => ({
        activeCages: [],
        locationName: '',
        onlyViewMode: false,
        allButtonState: { isSelected: false, isDisabled: false },
        startStream: () => {},
        stopStream: () => {},
        disableStream: () => {},

        initializeCages: (activeCages, startStream, stopStream, disableStream) =>
            set((state) => {
                state.startStream = startStream;
                state.stopStream = stopStream;
                state.disableStream = disableStream;
                state.activeCages = activeCages.map((cage) => ({
                    ...cage,
                    online: undefined,
                    isButtonDisabled: true,
                    isLoading: false,
                    icon: 'Offline',
                    // A list row carries no mux state; applyLocationStreams selects the pens
                    // that are already streaming right after this.
                    isButtonSelected: false,
                }));
            }),

        setIsButtonDisabled: (bioscopeId, isButtonDisabled) =>
            set((state) => {
                if (bioscopeId === 0) {
                    state.allButtonState.isDisabled = isButtonDisabled;
                } else {
                    const cage = state.activeCages.find((c) => c.bioscopeId === bioscopeId);
                    if (cage) {
                        cage.isButtonDisabled = isButtonDisabled;
                    }
                }
            }),

        setOnlyViewMode: (onlyViewMode) =>
            set((state) => {
                state.onlyViewMode = onlyViewMode;
            }),

        updateBioscopeOnlineStatus: (bioscopeId, status) =>
            set((state) => {
                const cage = state.activeCages.find((c) => c.bioscopeId === bioscopeId);
                if (!cage) return;

                const isOnline = status === 'online';
                cage.online = isOnline;

                if (!isOnline) {
                    cage.isButtonDisabled = true;
                    return;
                }

                const { dailyQuotaAvailable } = cage;
                if (!dailyQuotaAvailable) {
                    cage.isButtonDisabled = true;
                    return;
                }

                const hasValidVersion = dailyQuotaAvailable.bioscopeVersion !== '1.0';
                const hasAvailableQuota = dailyQuotaAvailable.quotaAvailable;

                cage.isInvalidBioscope = !hasValidVersion;
                cage.isOutOfQuota = !hasAvailableQuota;
                cage.isButtonDisabled = !hasValidVersion || !hasAvailableQuota;
            }),

        buttonActionHandler: (bioscopeId, v) =>
            set((state) => {
                const cage = state.activeCages.find((c) => c.bioscopeId === bioscopeId);
                if (cage) {
                    if (typeof v !== 'undefined') {
                        cage.isButtonSelected = v;
                    } else {
                        cage.isButtonSelected = !cage.isButtonSelected;
                    }
                    if (cage.isButtonSelected === true) {
                        state.startStream({ bioscopeId: cage.bioscopeId });
                    } else if (cage.isButtonSelected === false) {
                        state.stopStream({ bioscopeId: cage.bioscopeId });
                    }
                }
            }),

        updateMuxData: (bioscopeId, muxData) =>
            set((state) => {
                const cage = state.activeCages.find((c) => c.bioscopeId === bioscopeId);
                if (cage) {
                    cage.muxData = muxData;
                }
            }),

        updateAllButtonState: () =>
            set((state) => {
                const flag = state.activeCages
                    .filter((cage) => cage.online && !cage.isOutOfQuota)
                    .every((cage) => cage.isButtonSelected);
                state.allButtonState.isSelected = flag;
            }),

        updateBioscopeData: (bioscopeId, data) =>
            set((state) => {
                const cage = state.activeCages.find((c) => c.bioscopeId === bioscopeId);
                if (cage) {
                    cage.bioscopeData = data;
                }
            }),
    }))
);
